import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Lock, ArrowLeft } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const SESSION_KEY = "im_unlocked";  // session-only unlock memory

/**
 * Optional access gate for the Item Master page.
 *
 * The gate is configured at /customization → Item Master Access Password.
 * If no password is set on this tenant, children render immediately.
 * If a password is set, the user must enter it once per session (sessionStorage
 * remembers it until the tab closes — same UX OperationGuard had).
 */
export default function ItemMasterGuard({ children }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [required, setRequired] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/item-master-password`, { skipPeriod: true });
        if (cancelled) return;
        const need = !!res.data?.required;
        setRequired(need);
        if (!need) {
          setUnlocked(true);
        } else {
          // Session-persisted unlock: skip prompt within the same session.
          try {
            if (sessionStorage.getItem(SESSION_KEY) === "1") setUnlocked(true);
          } catch {/* ignore */}
        }
      } catch {
        // Fail open — never lock the user out if the check itself fails.
        if (!cancelled) { setRequired(false); setUnlocked(true); }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setError("Please enter the password"); return; }
    try {
      const res = await axios.post(`${API}/settings/item-master-password/verify`,
        { password: password.trim() }, { skipPeriod: true });
      if (res.data?.valid) {
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {/* ignore */}
        setUnlocked(true);
        setError("");
      } else {
        setError("Wrong password — try again");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Verification failed");
    }
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-sm text-[#64748B]">Loading…</div>
    </div>;
  }
  if (unlocked || !required) return children;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="item-master-gate">
      <Card className="p-8 w-full max-w-md border border-[#E2E8F0] rounded-xl shadow-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#0F766E] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={28} weight="bold" className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-[#0F172A]" style={{fontFamily: 'Chivo'}}>Item Master</h2>
          <p className="text-sm text-[#64748B] mt-2">Enter the access password to continue</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Password</Label>
            <Input type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter access password"
              className="min-h-[48px] rounded-lg text-center text-lg tracking-widest" autoFocus
              data-testid="item-master-pwd-input" />
            {error && <p className="text-sm text-[#EF4444] text-center">{error}</p>}
          </div>
          <Button type="submit" className="w-full bg-[#0F766E] hover:bg-[#0D5F58] text-white min-h-[48px] rounded-lg font-semibold" data-testid="item-master-unlock-btn">
            Unlock
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/")}
            className="w-full min-h-[44px] text-[#64748B] border-[#E2E8F0] rounded-lg">
            <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
          </Button>
        </form>
      </Card>
    </div>
  );
}
